import React, { useState, useEffect } from 'react';
import axios from 'axios';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import './Support.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function Support({ user }) {
  const [tickets, setTickets] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryFields, setCategoryFields] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [activeTab, setActiveTab] = useState('active'); // 'active' or 'closed'
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [formData, setFormData] = useState({
    category_id: '',
    script_id: '',
    subject: '',
    custom_data: {}
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedTicket) {
      fetchMessages(selectedTicket.id);
      const interval = setInterval(() => {
        fetchMessages(selectedTicket.id);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [selectedTicket]);

  useEffect(() => {
    if (formData.category_id) {
      fetchCategoryFields(formData.category_id);
    } else {
      setCategoryFields([]);
    }
  }, [formData.category_id]);

  const fetchData = async () => {
    try {
      console.log('Starting to fetch support data...');
      
      const ticketsResponse = await axios.get(`${API_URL}/api/support`, { withCredentials: true });
      console.log('✓ Fetched tickets:', ticketsResponse.data);
      setTickets(ticketsResponse.data);
      
      const scriptsResponse = await axios.get(`${API_URL}/api/scripts`);
      console.log('✓ Fetched scripts:', scriptsResponse.data);
      setScripts(scriptsResponse.data);
      
      const categoriesResponse = await axios.get(`${API_URL}/api/support/categories`, { withCredentials: true });
      console.log('✓ Fetched categories:', categoriesResponse.data);
      setCategories(categoriesResponse.data);
      
      console.log('All data fetched successfully!');
      setLoading(false);
    } catch (error) {
      console.error('❌ Error fetching data:', error);
      console.error('Error details:', error.response?.data);
      setLoading(false);
    }
  };

  const fetchCategoryFields = async (categoryId) => {
    try {
      const response = await axios.get(`${API_URL}/api/support/categories/${categoryId}/fields`);
      setCategoryFields(response.data);
      // Reset custom data when category changes
      setFormData(prev => ({ ...prev, custom_data: {} }));
    } catch (error) {
      console.error('Error fetching category fields:', error);
    }
  };

  const fetchMessages = async (ticketId) => {
    try {
      const response = await axios.get(`${API_URL}/api/support/${ticketId}/messages`, {
        withCredentials: true
      });
      console.log('📨 Messages loaded:', response.data);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if user already has an active ticket
    const hasActiveTicket = tickets.some(t => 
      t.user_id === user.id && (t.status === 'open' || t.status === 'in_progress')
    );
    
    if (hasActiveTicket && user.role !== 'admin') {
      alert('You already have an active ticket. Please close it before creating a new one.');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/support`, formData, {
        withCredentials: true
      });
      setFormData({ category_id: '', script_id: '', subject: '', custom_data: {} });
      setCategoryFields([]);
      setShowForm(false);
      fetchData();
    } catch (error) {
      console.error('Error creating ticket:', error);
      alert('Error creating ticket: ' + error.response?.data?.error);
    }
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      await axios.put(
        `${API_URL}/api/support/${ticketId}`,
        { status: newStatus },
        { withCredentials: true }
      );
      fetchData();
      if (selectedTicket && selectedTicket.id === ticketId) {
        setSelectedTicket({ ...selectedTicket, status: newStatus });
      }
    } catch (error) {
      console.error('Error updating ticket:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() && !attachmentFile) return;

    try {
      const formData = new FormData();
      formData.append('message', newMessage);
      if (attachmentFile) {
        formData.append('attachment', attachmentFile);
      }

      await axios.post(
        `${API_URL}/api/support/${selectedTicket.id}/messages`,
        formData,
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setNewMessage('');
      setAttachmentFile(null);
      fetchMessages(selectedTicket.id);
      fetchData();
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleCustomFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      custom_data: {
        ...prev.custom_data,
        [fieldName]: value
      }
    }));
  };

  const renderCustomField = (field) => {
    const value = formData.custom_data[field.field_name] || '';
    
    switch (field.field_type) {
      case 'text':
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleCustomFieldChange(field.field_name, e.target.value)}
            required={field.is_required}
            placeholder={`Enter ${field.field_name.toLowerCase()}`}
          />
        );
      
      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleCustomFieldChange(field.field_name, e.target.value)}
            required={field.is_required}
            placeholder={`Enter ${field.field_name.toLowerCase()}`}
            rows="4"
          />
        );
      
      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleCustomFieldChange(field.field_name, e.target.value)}
            required={field.is_required}
            placeholder={`Enter ${field.field_name.toLowerCase()}`}
          />
        );
      
      case 'select':
        const options = field.field_options ? field.field_options.split(',').map(opt => opt.trim()) : [];
        return (
          <select
            value={value}
            onChange={(e) => handleCustomFieldChange(field.field_name, e.target.value)}
            required={field.is_required}
          >
            <option value="">Select an option...</option>
            {options.map((option, index) => (
              <option key={index} value={option}>{option}</option>
            ))}
          </select>
        );
      
      case 'checkbox':
        return (
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={value === true || value === 'true'}
              onChange={(e) => handleCustomFieldChange(field.field_name, e.target.checked)}
            />
            Yes
          </label>
        );
      
      default:
        return null;
    }
  };

  const handleEmojiSelect = (emoji) => {
    setNewMessage(newMessage + emoji.native);
    setShowEmojiPicker(false);
  };

  const openMediaModal = (attachment, type) => {
    console.log('🖼️ Opening media modal:', { attachment, type });
    setSelectedMedia({ url: attachment, type: type });
    setShowMediaModal(true);
  };

  const closeMediaModal = () => {
    console.log('❌ Closing media modal');
    setShowMediaModal(false);
    setSelectedMedia(null);
  };

  const parseEmojis = (text) => {
    const emojiMap = {
      ':small_blue_diamond:': '🔹',
      ':large_blue_diamond:': '🔷',
      ':small_orange_diamond:': '🔸',
      ':large_orange_diamond:': '🔶',
      ':white_check_mark:': '✅',
      ':x:': '❌',
      ':heavy_check_mark:': '✔️',
      ':star:': '⭐',
      ':rocket:': '🚀',
      ':fire:': '🔥',
      ':warning:': '⚠️',
      ':information_source:': 'ℹ️',
      ':bulb:': '💡',
      ':gear:': '⚙️',
      ':wrench:': '🔧',
      ':hammer:': '🔨',
      ':package:': '📦',
      ':clipboard:': '📋',
    };
    
    let parsedText = text;
    Object.keys(emojiMap).forEach(key => {
      parsedText = parsedText.replace(new RegExp(key, 'g'), emojiMap[key]);
    });
    return parsedText;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'status-open';
      case 'in_progress': return 'status-progress';
      case 'closed': return 'status-closed';
      default: return '';
    }
  };

  const formatStatus = (status) => {
    switch (status) {
      case 'in_progress': return 'In Progress';
      case 'open': return 'Open';
      case 'closed': return 'Closed';
      default: return status;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return <div className="loading">Loading support tickets...</div>;
  }

  // Chat view when ticket is selected
  if (selectedTicket) {
    return (
      <>
      <div className="support-chat">
        <div className="chat-header glass">
          <button onClick={() => setSelectedTicket(null)} className="back-btn">
            ← Back to Tickets
          </button>
          <div className="chat-title">
            <h2>{selectedTicket.subject}</h2>
            <div className="chat-meta">
              {selectedTicket.category_name && (
                <span className="category-badge">📁 {selectedTicket.category_name}</span>
              )}
              {selectedTicket.script_title && (
                <span className="script-badge">🎮 {selectedTicket.script_title}</span>
              )}
              <span className={`status-badge ${getStatusColor(selectedTicket.status)}`}>
                {formatStatus(selectedTicket.status)}
              </span>
            </div>
          </div>
          {user?.role === 'admin' && (
            <div className="status-controls">
              <button 
                onClick={() => handleStatusChange(selectedTicket.id, 'open')}
                className={`btn btn-sm ${selectedTicket.status === 'open' ? 'btn-primary' : 'btn-secondary'}`}
              >
                Open
              </button>
              <button 
                onClick={() => handleStatusChange(selectedTicket.id, 'in_progress')}
                className={`btn btn-sm ${selectedTicket.status === 'in_progress' ? 'btn-primary' : 'btn-secondary'}`}
              >
                In Progress
              </button>
              <button 
                onClick={() => handleStatusChange(selectedTicket.id, 'closed')}
                className={`btn btn-sm ${selectedTicket.status === 'closed' ? 'btn-primary' : 'btn-secondary'}`}
              >
                Closed
              </button>
            </div>
          )}
        </div>

        <div className="chat-messages glass">
          {messages.length === 0 ? (
            <div className="no-messages">
              <p>No messages yet. Start the conversation!</p>
            </div>
          ) : (
            messages.map((msg) => (
              <div 
                key={msg.id} 
                className={`chat-message ${msg.user_id === user?.id ? 'own-message' : 'other-message'}`}
              >
                <div className="message-header">
                  <div className="message-info">
                    <span className="message-author">
                      {msg.username || 'You'}
                      {msg.role === 'admin' && <span className="admin-badge">Admin</span>}
                    </span>
                    <span className="message-time">{formatDate(msg.created_at)}</span>
                  </div>
                </div>
                <div className="message-content">
                  {msg.message && parseEmojis(msg.message)}
                  {msg.attachment && (
                    <div className="message-attachment">
                      {msg.attachment_type === 'image' ? (
                        <img 
                          src={`${API_URL}${msg.attachment}`} 
                          alt="Attachment" 
                          onClick={() => openMediaModal(msg.attachment, 'image')}
                          onError={(e) => {
                            console.error('Image load error:', msg.attachment);
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : msg.attachment_type === 'video' ? (
                        <div 
                          className="video-preview"
                          onClick={() => openMediaModal(msg.attachment, 'video')}
                        >
                          <video 
                            src={`${API_URL}${msg.attachment}`}
                            onError={(e) => {
                              console.error('Video load error:', msg.attachment);
                            }}
                          />
                          <div className="video-overlay">
                            <span className="play-icon">▶</span>
                          </div>
                        </div>
                      ) : (
                        <div className="attachment-fallback">
                          <a 
                            href={`${API_URL}${msg.attachment}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="attachment-link"
                          >
                            📎 View Attachment
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        <form onSubmit={handleSendMessage} className="chat-input glass">
          {attachmentFile && (
            <div className="attachment-preview">
              <div className="preview-header">
                <span>📎 Attached file:</span>
                <button 
                  type="button" 
                  onClick={() => setAttachmentFile(null)} 
                  className="remove-attachment"
                  title="Remove attachment"
                >
                  ✕
                </button>
              </div>
              <div className="preview-content">
                {attachmentFile.type.startsWith('image/') ? (
                  <img 
                    src={URL.createObjectURL(attachmentFile)} 
                    alt="Preview" 
                    className="preview-image"
                  />
                ) : attachmentFile.type.startsWith('video/') ? (
                  <video 
                    src={URL.createObjectURL(attachmentFile)} 
                    className="preview-video"
                    controls
                  />
                ) : (
                  <div className="preview-file">
                    <span className="file-icon">📄</span>
                    <span className="file-name">{attachmentFile.name}</span>
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="input-container">
            <button
              type="button"
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              className="emoji-button"
            >
              😊
            </button>
            {showEmojiPicker && (
              <div className="emoji-picker-container">
                <Picker data={data} onEmojiSelect={handleEmojiSelect} theme="dark" />
              </div>
            )}
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type your message..."
              className="message-input"
              rows="1"
            />
            <label className="attachment-button">
              📎
              <input
                type="file"
                accept="image/*,video/*"
                onChange={(e) => setAttachmentFile(e.target.files[0])}
              />
            </label>
            <button type="submit" className="send-button btn btn-primary">Send</button>
          </div>
        </form>
      </div>
      {showMediaModal && selectedMedia && (
        <div className="media-modal-backdrop" onClick={closeMediaModal}>
          <div className="media-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="media-close-btn" onClick={closeMediaModal}>
              ✕
            </button>
            {selectedMedia.type === 'image' ? (
              <img 
                src={`${API_URL}${selectedMedia.url}`} 
                alt="Full size" 
                className="modal-media-image"
              />
            ) : (
              <video 
                src={`${API_URL}${selectedMedia.url}`} 
                controls 
                autoPlay
                className="modal-media-video"
              />
            )}
          </div>
        </div>
      )}
      </>
    );
  }

  // Ticket list view
  return (
    <div className="support">
      <div className="panel-header">
        <h1>Support Tickets</h1>
        <button 
          onClick={() => setShowForm(!showForm)} 
          className="btn btn-primary"
          disabled={user?.role !== 'admin' && tickets.some(t => 
            t.user_id === user?.id && (t.status === 'open' || t.status === 'in_progress')
          )}
          title={
            user?.role !== 'admin' && tickets.some(t => 
              t.user_id === user?.id && (t.status === 'open' || t.status === 'in_progress')
            ) ? 'Close your active ticket first' : 'Create a new support ticket'
          }
        >
          Create Ticket
        </button>
      </div>

      {/* Tab filters */}
      <div className="ticket-tabs">
        <button 
          className={`tab-btn ${activeTab === 'active' ? 'active' : ''}`}
          onClick={() => setActiveTab('active')}
        >
          Active Tickets
          <span className="tab-count">
            {tickets.filter(t => t.status === 'open' || t.status === 'in_progress').length}
          </span>
        </button>
        <button 
          className={`tab-btn ${activeTab === 'closed' ? 'active' : ''}`}
          onClick={() => setActiveTab('closed')}
        >
          Closed Tickets
          <span className="tab-count">
            {tickets.filter(t => t.status === 'closed').length}
          </span>
        </button>
      </div>

      {showForm && (
        <div className="modal-backdrop" onClick={() => setShowForm(false)}>
          <div className="ticket-form-modal glass" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New Ticket</h2>
              <button className="close-btn" onClick={() => setShowForm(false)}>✕</button>
            </div>
            
            <div className="modal-body">
              {categories.length === 0 && (
                <div className="alert alert-warning">
                  ⚠️ No categories available. Please ask an admin to create support categories first.
                </div>
              )}
              
              {scripts.length === 0 && (
                <div className="alert alert-info">
                  ℹ️ No scripts available yet.
                </div>
              )}
              
              <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Category *</label>
              <select
                value={formData.category_id}
                onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                required
                disabled={categories.length === 0}
              >
                <option value="">
                  {categories.length === 0 ? 'No categories available' : 'Select a category...'}
                </option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                    {category.required_role === 'admin' && ' (Admin Only)'}
                  </option>
                ))}
              </select>
              {formData.category_id && categories.find(c => c.id === parseInt(formData.category_id))?.description && (
                <small className="category-description">
                  {categories.find(c => c.id === parseInt(formData.category_id)).description}
                </small>
              )}
            </div>

            <div className="form-group">
              <label>Related Script (Optional)</label>
              <select
                value={formData.script_id}
                onChange={(e) => setFormData({ ...formData, script_id: e.target.value })}
              >
                <option value="">No specific script</option>
                {scripts.map((script) => (
                  <option key={script.id} value={script.id}>
                    {script.title}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Subject *</label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                required
                placeholder="Brief description of your issue"
              />
            </div>

            {categoryFields.length > 0 && (
              <div className="custom-fields-section">
                <h3>Additional Information</h3>
                {categoryFields
                  .sort((a, b) => a.order_index - b.order_index)
                  .map((field) => (
                    <div key={field.id} className="form-group">
                      <label>
                        {field.field_name}
                        {field.is_required && ' *'}
                      </label>
                      {renderCustomField(field)}
                    </div>
                  ))}
              </div>
            )}

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                Create Ticket
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="btn btn-secondary">
                Cancel
              </button>
            </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <div className="tickets-list">
        {tickets.filter(t => 
          activeTab === 'active' 
            ? (t.status === 'open' || t.status === 'in_progress')
            : t.status === 'closed'
        ).length === 0 ? (
          <div className="empty-state glass">
            <div className="empty-icon">🎫</div>
            <h3>No {activeTab === 'active' ? 'Active' : 'Closed'} Tickets</h3>
            <p>
              {activeTab === 'active' 
                ? 'Create your first ticket to get help from our team'
                : 'Closed tickets will appear here'
              }
            </p>
          </div>
        ) : (
          tickets
            .filter(t => 
              activeTab === 'active' 
                ? (t.status === 'open' || t.status === 'in_progress')
                : t.status === 'closed'
            )
            .map((ticket) => (
            <div 
              key={ticket.id} 
              className="ticket-card glass fade-in"
              onClick={() => setSelectedTicket(ticket)}
              style={{ cursor: 'pointer' }}
            >
              <div className="ticket-header">
                <h3>{ticket.subject}</h3>
                <span className={`ticket-status ${getStatusColor(ticket.status)}`}>
                  {formatStatus(ticket.status)}
                </span>
              </div>
              <div className="ticket-meta">
                {ticket.category_name && (
                  <span className="meta-item">📁 {ticket.category_name}</span>
                )}
                {ticket.script_title && (
                  <span className="meta-item">🎮 {ticket.script_title}</span>
                )}
                {user?.role === 'admin' && ticket.username && (
                  <span className="meta-item">👤 {ticket.username}</span>
                )}
                <span className="meta-item">📅 {formatDate(ticket.created_at)}</span>
              </div>
            </div>
          ))
        )}
      </div>
      {showMediaModal && selectedMedia && (
        <div className="media-modal-backdrop" onClick={closeMediaModal}>
          <div className="media-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="media-close-btn" onClick={closeMediaModal}>
              ✕
            </button>
            {selectedMedia.type === 'image' ? (
              <img 
                src={`${API_URL}${selectedMedia.url}`} 
                alt="Full size" 
                className="modal-media-image"
              />
            ) : (
              <video 
                src={`${API_URL}${selectedMedia.url}`} 
                controls 
                autoPlay
                className="modal-media-video"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Support;
